import { useRef, useEffect } from 'react';
import { DataTable, TextInput, Switch, toast } from '@etherisc/ui-kit';
import { PositiveButton } from '../../components/buttons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUsers } from '../../hooks/useUsers';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../lib/casl/hooks';
import { getRoleDisplayName } from '../../constants/roles';
import { userService } from '../../services/userService';
import { ApiError } from '../../utils/errorHandling';
import { PermissionGate } from '../../components/PermissionGate';
import { createSmartPaginationConfig } from '../../utils/tableHelpers';
import type { User } from '../../types/user';
import type { UserRole } from '../../types/Auth';
import type { ActionDefinition } from '../../utils/tableHelpers';

// ✅ NEW: Import ALL column patterns for 100% pattern-based columns
import { 
  textColumn,
  actionsColumn,
  avatarColumn,
  statusColumn,
  dateColumn,
  activeColumn
} from '../../utils/columns';

import {
  SearchIcon,
  UsersIcon,
  FolderIcon,
  EditIcon,
  TrashIcon,
} from 'lucide-react';

export function UserManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  
  const {
    filteredUsers,
    error,
    searchQuery,
    setSearchQuery,
    totalUsers,
    activeUsers,
    inactiveUsers,
    refreshUsers,
    scope,
    canViewAllUsers,
    requestAllUsers,
  } = useUsers();

  // Restore scope from URL params on mount
  const initialScopeApplied = useRef(false);
  useEffect(() => {
    if (!initialScopeApplied.current && canViewAllUsers && searchParams.get('scope') === 'all' && scope !== 'all') {
      initialScopeApplied.current = true;
      requestAllUsers();
    }
  }, [canViewAllUsers, searchParams, scope, requestAllUsers]);

  // Smart pagination configuration for complex table (avatars, badges, rich UI)
  // NOTE: ui-kit 0.6.1 DataTable pagination has known issues (layout, state sync)
  // ui-kit team is working on new DataTable implementation - keep current setup until fixed
  const paginationSetup = createSmartPaginationConfig(filteredUsers.length, 'COMPLEX');
  
  // Log validation warnings in development
  if (process.env.NODE_ENV === 'development' && paginationSetup.validation) {
    console.warn('UserManagement pagination:', paginationSetup.validation);
  }

  const handleEdit = (user: User) => {
    navigate(`/admin/users/${user.id}/edit`);
  };

  const handleDelete = async (targetUser: User) => {
    // Additional client-side validation
    if (targetUser.id === user?.id) {
      showError('Delete Failed', 'You cannot delete your own account.');
      return;
    }

    if (targetUser.roles?.includes('admin')) {
      showError('Delete Failed', 'Admin users cannot be deleted for security reasons.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete user "${targetUser.name || targetUser.email}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      await userService.deleteUser(targetUser.id);
      showSuccess('User Deleted', `User ${targetUser.name || targetUser.email} has been successfully deleted.`);
      // Refresh the user list
      await refreshUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      
      if (error instanceof ApiError) {
        showError('Delete Failed', error.message);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete user. Please try again.';
        showError('Delete Failed', errorMessage);
      }
    }
  };

  const handleCreateUser = () => {
    navigate('/admin/users/create');
  };

  // Define action definitions for the table
  const actionDefinitions: ActionDefinition<User>[] = [
    {
      id: 'edit',
      label: 'Edit User',
      icon: <EditIcon size={16} />,
      onClick: handleEdit,
      intent: 'outline',
      hidden: () => !can('update', 'User'),
    },
    {
      id: 'delete',
      label: 'Delete User',
      icon: <TrashIcon size={16} />,
      onClick: handleDelete,
      intent: 'danger',
      disabled: (targetUser) => {
        // Cannot delete current user or admin users
        return targetUser.id === user?.id || (targetUser.roles?.includes('admin') ?? false);
      },
      hidden: () => !can('delete', 'User'),
    },
  ];

  // Column order: Initials, Name, Role, Organisation, Status, Email, Created, Actions
  const columns = [
    avatarColumn<User>('avatar', {
      header: '',
      sortable: false,
      resizable: false,
      width: 60,
      nameAccessor: 'name',
      size: 'sm'
    }),
    
    textColumn<User>('name', {
      header: 'Name',
      getValue: (user) => {
        const u = user as User;
        const displayName = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim();
        return displayName || 'Unknown User';
      }
    }),
    
    statusColumn<User>('roles', {
      header: 'Role',
      getValue: (user) => {
        const u = user as User;
        return (u.roles && u.roles[0]) || 'user';
      },
      transformLabel: (value) => getRoleDisplayName(value as UserRole)
    }),
    
    textColumn<User>('organization', {
      header: 'Organisation',
      getValue: (user) => {
        const u = user as User;
        return u.organization.name;
      }
    }),
    
    activeColumn<User>('isActive', 'Status'),
    
    textColumn<User>('email', {
      header: 'Email'
    }),
    
    dateColumn<User>('createdAt', {
      header: 'Created',
      format: 'relative'
    }),
    
    actionsColumn<User>(actionDefinitions, {
      size: 'md'
    }),
  ];

  if (error) {
    return (
      <div className="p-6">
        <div className="alert alert-error">
          <span>Error loading users: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-base-content/70 mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <PermissionGate permission="users:create">
          <PositiveButton onClick={handleCreateUser}>
            Add User
          </PositiveButton>
        </PermissionGate>
      </div>

      {/* Scope Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 text-blue-600 mt-0.5">
            {scope === 'all' ? <UsersIcon size={20} /> : <FolderIcon size={20} />}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900">
              {scope === 'all' ? 'All Organisations View' : 'Organisation View'}
            </h3>
            <p className="text-xs text-blue-700 mt-1">
              {scope === 'all' 
                ? `Showing users from all organisations (${totalUsers} total)`
                : `Showing users from your organisation only (${totalUsers} users)`}
            </p>
            {canViewAllUsers && (
              <div className="mt-2 text-xs text-blue-600">
                <span className="font-medium">Admin Access:</span> You can view users from all organisations or filter by your organisation.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary">{totalUsers}</div>
          <div className="text-sm text-base-content/70">Total Users</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-success">{activeUsers}</div>
          <div className="text-sm text-base-content/70">Active Users</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-error">{inactiveUsers}</div>
          <div className="text-sm text-base-content/70">Inactive Users</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <SearchIcon className="w-5 h-5" />
            Search and Filters
          </h3>
          {canViewAllUsers && (
            <div className="flex items-center gap-2">
              <Switch
                checked={scope === 'all'}
                onCheckedChange={(checked) => {
                  if (checked) {
                    requestAllUsers();
                    setSearchParams(prev => { prev.set('scope', 'all'); return prev; });
                  } else {
                    refreshUsers();
                    setSearchParams(prev => { prev.delete('scope'); return prev; });
                  }
                }}
                id="view-all-orgs"
              />
              <label htmlFor="view-all-orgs" className="text-sm font-medium text-gray-700 cursor-pointer">
                View users from all organisations
              </label>
              {scope === 'all' && (
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Admin Mode: All Organisations
                </span>
              )}
            </div>
          )}
        </div>
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
          <div className="pl-10">
            <TextInput
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredUsers}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        pagination={paginationSetup.config}
      />
    </div>
  );
} 